import { useState, useCallback, useRef, useEffect } from 'react';
import { getApiToken } from '../../../../context/AuthContext';
import { Message, QuickAction, ToolCallState, DataCard, Widget } from './types';
import { getRegistryItemById } from '../CommandBar/registry';
import { captureAiChatFeedback } from './feedbackAnalytics';

const AI_CHAT_URL = '/api/ai-chat/message';
const AI_CHAT_FEEDBACK_URL = '/api/ai-chat/feedback';
const MESSAGE_LIMIT = 20;

const FRIENDLY_ERRORS: Record<string, string> = {
  token_expired:
    'Your session has expired. Please refresh the page and try again.',
  unauthorized: 'You are not authorized. Please log in and try again.',
  missing_body: 'Something went wrong. Please try again later.',
  invalid_json: 'Something went wrong. Please try again later.',
  invalid_request_type: 'Something went wrong. Please try again later.',
};

// Shown for technical failures the user can't act on (HTTP status, thrown
// exceptions) — the raw detail is logged to the console, never rendered.
const GENERIC_ERROR = 'Something went wrong. Please try again.';

// Guards that reject the message itself. A rejected message must not stay in
// the thread: the whole history is re-sent on every turn, so one over-long
// message made the server reject every later question too — the conversation
// was bricked until the user started a new chat (found in the FLA-8172 v2 run).
const MESSAGE_REJECTED_CODES = new Set([
  'message_too_long',
  'conversation_too_long',
  'rate_limit_exceeded',
]);

// The map is keyed by CODE. It used to be handed `data.message`, so nothing
// ever matched and every error rendered as GENERIC_ERROR — including the input
// guards, whose messages already tell the user exactly what to do (FLA-8210).
function friendlyError(code: string, message: string): string {
  const mapped = FRIENDLY_ERRORS[code];
  if (mapped) return mapped;
  // A coded error is a deliberate, user-facing one — the input guards write
  // their own copy ("Message is too long (4439 chars)…") and it is better than
  // anything we could substitute. An error with NO code is a technical failure
  // whose message can carry SOQL, stack traces or record ids, so it never
  // reaches the user.
  if (code && message) return message;
  console.error('AI chat unmapped error', code || message);
  return GENERIC_ERROR;
}

function generateId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function createEmptyMessage(
  role: 'user' | 'assistant',
  content: string = '',
): Message {
  return {
    id: generateId(),
    role,
    content,
    toolCalls: [],
    dataCards: [],
    widgets: [],
    quickActions: [],
  };
}

function getMessagesForApi(
  messages: Message[],
): Array<{ role: string; content: string }> {
  return messages.map((m) => ({ role: m.role, content: m.content }));
}

function isRecordObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// A multi-tool answer that hit the same tool twice used to stack two identical
// cards on top of each other (FLA-8234). A repeat now replaces the card it
// repeats. Matching on the full payload, not just the type, so two genuinely
// different cards of the same kind (two people, say) both survive.
function appendWidget(widgets: Widget[], next: Widget): Widget[] {
  const nextKey = JSON.stringify(next);
  const index = widgets.findIndex((w) => JSON.stringify(w) === nextKey);
  if (index === -1) return [...widgets, next];
  const updated = [...widgets];
  updated[index] = next;
  return updated;
}

function tryParseDataCard(result: string): DataCard | null {
  try {
    const parsed = JSON.parse(result);
    if (
      parsed &&
      typeof parsed.title === 'string' &&
      Array.isArray(parsed.rows)
    ) {
      return parsed;
    }
  } catch {
    // not valid JSON or not a DataCard
  }
  return null;
}

export async function parseSSEStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onEvent: (eventName: string, data: string) => void,
): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() ?? '';
    for (const part of parts) {
      let eventName = '';
      let data = '';
      for (const line of part.split('\n')) {
        if (line.startsWith('event: ')) eventName = line.slice(7);
        if (line.startsWith('data: ')) data = line.slice(6);
      }
      if (eventName && data) onEvent(eventName, data);
    }
  }
}

type SSEEventData = Record<string, unknown>;

function parseEventData(data: string): SSEEventData {
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
}

export function useAiChat(): {
  messages: Message[];
  isLoading: boolean;
  // Text the employee submitted mid-stream, waiting to be sent.
  pendingMessage: string | null;
  pendingConfirm: ToolCallState | null;
  error: string | null;
  activeToolName: string | null;
  // Returns false when the message was not sent — queued behind a stream, or
  // refused because the thread is at its limit.
  sendMessage: (text: string) => boolean;
  confirmTool: (toolId: string) => void;
  cancelTool: (toolId: string) => void;
  reset: () => void;
  retry: () => void;
  restoreMessages: (msgs: Message[]) => void;
  submitFeedback: (
    messageId: string,
    rating: 'up' | 'down',
    comment?: string,
  ) => Promise<void>;
  messageCount: number;
} {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // At most one message waits behind the current stream. The ref mirrors
  // isLoading so two sends in the same tick still see the first one in flight —
  // state has not re-rendered yet at that point.
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const isStreamingRef = useRef(false);
  // Set when the server refuses the turn and we drop it from the thread. The
  // stream does not stop there, and a later message_done would otherwise
  // attach this run's quick actions to the previous answer.
  const turnRejectedRef = useRef(false);
  const [activeToolName, setActiveToolName] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Set when a tool finishes so the next text delta starts a new paragraph
  // instead of gluing the post-tool answer onto the pre-tool narration.
  const needsParagraphBreakRef = useRef(false);

  const pendingConfirm: ToolCallState | null =
    messages
      .flatMap((m) => m.toolCalls)
      .filter((tc) => tc.status === 'confirm_required')
      .pop() ?? null;

  const updateLastAssistantMessage = (
    updater: (msg: Message) => Message,
  ): void => {
    setMessages((prev) => {
      const lastIdx = prev.length - 1;
      if (lastIdx < 0 || prev[lastIdx].role !== 'assistant') return prev;
      const updated = [...prev];
      updated[lastIdx] = updater(updated[lastIdx]);
      return updated;
    });
  };

  const handleSSEEvent = useCallback(
    (eventName: string, rawData: string): void => {
      const data = parseEventData(rawData);

      switch (eventName) {
        case 'run_started': {
          const runId = typeof data.runId === 'string' ? data.runId : undefined;
          if (runId) {
            updateLastAssistantMessage((msg) => ({ ...msg, runId }));
          }
          break;
        }
        case 'text_delta': {
          const content = typeof data.content === 'string' ? data.content : '';
          const breakNeeded = needsParagraphBreakRef.current;
          needsParagraphBreakRef.current = false;
          updateLastAssistantMessage((msg) => ({
            ...msg,
            content:
              msg.content +
              (breakNeeded &&
              msg.content.length > 0 &&
              !msg.content.endsWith('\n\n')
                ? '\n\n'
                : '') +
              content,
          }));
          break;
        }
        case 'tool_start': {
          const toolName =
            typeof data.toolName === 'string' ? data.toolName : 'unknown';
          const toolCall: ToolCallState = {
            id: typeof data.id === 'string' ? data.id : generateId(),
            toolName,
            input: isRecordObject(data.input) ? data.input : {},
            status: 'running',
          };
          setActiveToolName(toolName);
          updateLastAssistantMessage((msg) => ({
            ...msg,
            toolCalls: [...msg.toolCalls, toolCall],
          }));
          break;
        }
        case 'tool_done': {
          setActiveToolName(null);
          needsParagraphBreakRef.current = true;
          const toolId = typeof data.id === 'string' ? data.id : '';
          const result = typeof data.result === 'string' ? data.result : '';
          const widget =
            isRecordObject(data.widget) &&
            typeof (data.widget as Record<string, unknown>).type === 'string'
              ? (data.widget as Widget)
              : null;
          updateLastAssistantMessage((msg) => {
            const dataCard = tryParseDataCard(result);
            return {
              ...msg,
              toolCalls: msg.toolCalls.map((tc) =>
                tc.id === toolId ? { ...tc, status: 'done', result } : tc,
              ),
              dataCards: dataCard
                ? [...msg.dataCards, dataCard]
                : msg.dataCards,
              widgets: widget ? appendWidget(msg.widgets, widget) : msg.widgets,
            };
          });
          break;
        }
        case 'tool_confirm_required': {
          const toolId = typeof data.id === 'string' ? data.id : generateId();
          const toolName =
            typeof data.toolName === 'string' ? data.toolName : 'unknown';
          const input = isRecordObject(data.input) ? data.input : {};
          const summary = typeof data.summary === 'string' ? data.summary : '';
          const widget =
            isRecordObject(data.widget) &&
            typeof (data.widget as Record<string, unknown>).type === 'string'
              ? (data.widget as Widget)
              : null;
          updateLastAssistantMessage((msg) => {
            const exists = msg.toolCalls.some((tc) => tc.id === toolId);
            const updatedWidgets = widget
              ? appendWidget(msg.widgets, widget)
              : msg.widgets;
            if (exists) {
              return {
                ...msg,
                widgets: updatedWidgets,
                toolCalls: msg.toolCalls.map((tc) =>
                  tc.id === toolId
                    ? {
                        ...tc,
                        status: 'confirm_required',
                        confirmSummary: summary,
                      }
                    : tc,
                ),
              };
            }
            return {
              ...msg,
              widgets: updatedWidgets,
              toolCalls: [
                ...msg.toolCalls,
                {
                  id: toolId,
                  toolName,
                  input,
                  status: 'confirm_required',
                  confirmSummary: summary,
                },
              ],
            };
          });
          setIsLoading(false);
          break;
        }
        case 'message_done': {
          setActiveToolName(null);
          type ServerQuickAction = {
            id: string;
            kind: 'inline' | 'goto';
            promptTemplate?: string;
          };
          const rawActions: ServerQuickAction[] = Array.isArray(
            data.quickActions,
          )
            ? data.quickActions
                .map((item: unknown): ServerQuickAction | null => {
                  if (typeof item === 'string') {
                    return { id: item, kind: 'goto' };
                  }
                  if (
                    item &&
                    typeof item === 'object' &&
                    typeof (item as { id?: unknown }).id === 'string'
                  ) {
                    const obj = item as {
                      id: string;
                      kind?: unknown;
                      promptTemplate?: unknown;
                    };
                    const kind = obj.kind === 'inline' ? 'inline' : 'goto';
                    const promptTemplate =
                      typeof obj.promptTemplate === 'string'
                        ? obj.promptTemplate
                        : undefined;
                    return {
                      id: obj.id,
                      kind,
                      ...(promptTemplate ? { promptTemplate } : {}),
                    };
                  }
                  return null;
                })
                .filter((a): a is ServerQuickAction => a !== null)
            : [];
          const quickActions: QuickAction[] = rawActions
            .map((raw) => {
              const entry = getRegistryItemById(raw.id);
              if (!entry) return null;
              const action: QuickAction = {
                id: entry.id,
                type: entry.type as 'action' | 'goto',
                kind: raw.kind,
                label: entry.label,
                route: entry.route,
              };
              if (entry.icon) {
                action.icon = entry.icon;
              }
              if (raw.promptTemplate) {
                action.promptTemplate = raw.promptTemplate;
              }
              return action;
            })
            .filter((a): a is QuickAction => a !== null);
          if (!turnRejectedRef.current) {
            updateLastAssistantMessage((msg) => ({
              ...msg,
              quickActions,
            }));
          }
          setIsLoading(false);
          break;
        }
        case 'error': {
          const code = typeof data.code === 'string' ? data.code : '';
          const message = typeof data.message === 'string' ? data.message : '';
          setError(friendlyError(code, message));
          if (MESSAGE_REJECTED_CODES.has(code)) {
            turnRejectedRef.current = true;
            // Drop the refused message and its empty answer, so the next turn
            // starts from a history the server will accept.
            setMessages((prev) => {
              const next = [...prev];
              while (
                next.length > 0 &&
                next[next.length - 1].role === 'assistant' &&
                next[next.length - 1].content === ''
              ) {
                next.pop();
              }
              if (next.length > 0 && next[next.length - 1].role === 'user') {
                next.pop();
              }
              return next;
            });
          }
          setIsLoading(false);
          break;
        }
      }
    },
    [],
  );

  const processStream = useCallback(
    async (response: Response): Promise<void> => {
      if (!response.body) {
        console.error('AI chat response had no body');
        setError(GENERIC_ERROR);
        setIsLoading(false);
        return;
      }
      const reader = response.body.getReader();
      try {
        await parseSSEStream(reader, handleSSEEvent);
      } finally {
        reader.releaseLock();
      }
    },
    [handleSSEEvent],
  );

  const postAndStream = useCallback(
    async (body: Record<string, unknown>): Promise<void> => {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      needsParagraphBreakRef.current = false;

      try {
        const token = getApiToken();
        const response = await fetch(AI_CHAT_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: token } : {}),
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!response.ok) {
          console.error(
            `AI chat request failed with status ${response.status}`,
          );
          setError(GENERIC_ERROR);
          return;
        }

        await processStream(response);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        console.error('AI chat request error', err);
        setError(GENERIC_ERROR);
      } finally {
        // A stream that ends without message_done or error used to leave the
        // chat loading forever, so a queued message never flushed and the
        // "will send" note never cleared. Only the request that is still the
        // current one may clear it, though: retry aborts this stream and opens
        // the next in the same tick, and the loser's finally would otherwise
        // wipe the progress line off the turn that replaced it.
        if (abortControllerRef.current === controller) {
          setIsLoading(false);
          isStreamingRef.current = false;
        }
      }
    },
    [processStream],
  );

  const sendMessage = useCallback(
    (text: string): boolean => {
      if (messages.length >= MESSAGE_LIMIT) {
        setError('Message limit reached (20 messages). Please reset the chat.');
        return false;
      }

      // Sent while the previous answer is still streaming. It used to vanish
      // with no queue, no disabled state and no notice (FLA-8235); it now
      // waits its turn and the UI says so.
      if (isStreamingRef.current) {
        setPendingMessage(text);
        return false;
      }

      isStreamingRef.current = true;
      turnRejectedRef.current = false;
      setPendingMessage(null);
      setError(null);
      const userMessage = createEmptyMessage('user', text);
      const assistantMessage = createEmptyMessage('assistant');

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      setIsLoading(true);

      const apiMessages = [
        ...getMessagesForApi(messages),
        { role: 'user', content: text },
      ];

      postAndStream({
        type: 'message',
        message: text,
        messages: apiMessages,
      });
      return true;
    },
    [messages, postAndStream],
  );

  useEffect(() => {
    isStreamingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    if (isLoading || !pendingMessage) return;
    // A confirmation also clears the loading state. Flushing here would fire a
    // fresh request underneath an open confirm step.
    if (pendingConfirm) return;
    // Only forget the queued text once it has actually been accepted —
    // clearing first meant a message that hit the length limit vanished after
    // the UI had already promised to send it. But if it can never be accepted
    // (the thread is full), drop it rather than promise it forever; the limit
    // error is already on screen.
    if (sendMessage(pendingMessage) || messages.length >= MESSAGE_LIMIT) {
      setPendingMessage(null);
    }
  }, [isLoading, pendingMessage, pendingConfirm, messages.length, sendMessage]);

  const confirmTool = useCallback(
    (toolId: string): void => {
      const toolCall = messages
        .flatMap((m) => m.toolCalls)
        .find((tc) => tc.id === toolId && tc.status === 'confirm_required');
      if (!toolCall) return;

      setIsLoading(true);
      setError(null);

      setMessages((prev) =>
        prev.map((msg) => ({
          ...msg,
          toolCalls: msg.toolCalls.map((tc) =>
            tc.id === toolId ? { ...tc, status: 'submitted' as const } : tc,
          ),
        })),
      );

      // Append a new assistant message for the confirm response
      const assistantMessage = createEmptyMessage('assistant');
      setMessages((prev) => [...prev, assistantMessage]);

      postAndStream({
        type: 'confirm',
        toolId,
        messages: getMessagesForApi(messages),
      });
    },
    [messages, postAndStream],
  );

  const cancelTool = useCallback(
    (toolId: string): void => {
      const toolCall = messages
        .flatMap((m) => m.toolCalls)
        .find((tc) => tc.id === toolId && tc.status === 'confirm_required');
      if (!toolCall) return;

      setIsLoading(true);
      setError(null);

      setMessages((prev) =>
        prev.map((msg) => ({
          ...msg,
          toolCalls: msg.toolCalls.map((tc) =>
            tc.id === toolId ? { ...tc, status: 'cancelled' as const } : tc,
          ),
        })),
      );

      // Append a new assistant message for the cancel response
      const assistantMessage = createEmptyMessage('assistant');
      setMessages((prev) => [...prev, assistantMessage]);

      postAndStream({
        type: 'cancel',
        toolId,
        messages: getMessagesForApi(messages),
      });
    },
    [messages, postAndStream],
  );

  // Escape hatch for a turn that has gone quiet. Aborts the stream, drops the
  // assistant message that stalled, and asks the same question again — so a
  // hung run costs one click instead of a retype.
  const retry = useCallback((): void => {
    abortControllerRef.current?.abort();
    // Claim the stream: without this a send between the abort and the new
    // request slips past the guard and opens a second one.
    isStreamingRef.current = true;
    turnRejectedRef.current = false;

    let lastUserIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        lastUserIndex = i;
        break;
      }
    }
    if (lastUserIndex === -1) return;

    const history = messages.slice(0, lastUserIndex + 1);
    setMessages([...history, createEmptyMessage('assistant')]);
    setActiveToolName(null);
    setError(null);
    setIsLoading(true);

    postAndStream({
      type: 'message',
      message: messages[lastUserIndex].content,
      messages: getMessagesForApi(history),
    });
  }, [messages, postAndStream]);

  const submitFeedback = useCallback(
    async (
      messageId: string,
      rating: 'up' | 'down',
      comment?: string,
    ): Promise<void> => {
      const message = messages.find((m) => m.id === messageId);
      if (!message || !message.runId) return;

      // Recorded first, and content-free: the rating plus the shape of the
      // answer it judges. Every 👍/👎 used to be discarded while the UI showed
      // success (FLA-8199) — this is the half that cannot silently fail,
      // because it never leaves the browser's own analytics client.
      captureAiChatFeedback({
        rating,
        runId: message.runId,
        tools: message.toolCalls.map((tc) => tc.toolName),
        hadWidget: message.widgets.length > 0,
        hadNextStep: message.quickActions.length > 0,
        answerLength: message.content.length,
        // The turn this answer belongs to, not how long the thread has since
        // grown — a thumbs-up on the first answer was reported as turn 5.
        turn: messages
          .slice(0, messages.indexOf(message))
          .filter((m) => m.role === 'user').length,
      });

      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, feedback: rating } : m)),
      );
      try {
        const token = getApiToken();
        await fetch(AI_CHAT_FEEDBACK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: token } : {}),
          },
          body: JSON.stringify({
            runId: message.runId,
            rating,
            ...(comment ? { comment } : {}),
          }),
        });
      } catch {
        // Best effort. The rating is already in PostHog; this call only adds
        // the server-side trace, and losing it must not lose the signal.
      }
    },
    [messages],
  );

  const reset = useCallback((): void => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setMessages([]);
    setIsLoading(false);
    isStreamingRef.current = false;
    setPendingMessage(null);
    setError(null);
  }, []);

  const restoreMessages = useCallback((msgs: Message[]): void => {
    setMessages(msgs);
    setIsLoading(false);
    isStreamingRef.current = false;
    setPendingMessage(null);
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    pendingMessage,
    pendingConfirm,
    error,
    activeToolName,
    sendMessage,
    confirmTool,
    cancelTool,
    reset,
    retry,
    restoreMessages,
    submitFeedback,
    messageCount: messages.length,
  };
}
